
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

interface LearningSession {
  id: string;
  correct_answers: number;
  total_questions: number;
  time_per_question: Record<string, number>;
  created_at: string;
  user_id: string;
}

interface PerformanceMetrics {
  averageScore: number;
  consecutiveCorrect: number;
  timePerQuestion: number;
}

export const useAdaptiveDifficulty = (currentDifficulty: string) => {
  const [recommendedDifficulty, setRecommendedDifficulty] = useState<'easy' | 'medium' | 'hard'>(currentDifficulty as 'easy' | 'medium' | 'hard');

  const { data: recentPerformance } = useQuery({
    queryKey: ['recent_performance'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data: sessions, error } = await supabase
        .from('learning_sessions')
        .select('*')
        .eq('user_id', user.user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return sessions as LearningSession[];
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  useEffect(() => {
    if (!recentPerformance?.length) return;

    // Calculate performance metrics
    const metrics: PerformanceMetrics = {
      averageScore: recentPerformance.reduce((acc, session) => {
        const score = (session.correct_answers / session.total_questions) * 100;
        return acc + score;
      }, 0) / recentPerformance.length,
      consecutiveCorrect: recentPerformance[0].correct_answers,
      timePerQuestion: Object.values(recentPerformance[0].time_per_question || {}).reduce((a, b) => a + b, 0) / 
        Math.max(1, Object.keys(recentPerformance[0].time_per_question || {}).length)
    };

    // Determine new difficulty based on performance
    let newDifficulty: 'easy' | 'medium' | 'hard' = currentDifficulty as 'easy' | 'medium' | 'hard';

    if (metrics.averageScore > 85 && metrics.consecutiveCorrect >= 3) {
      // Increase difficulty if performing very well
      newDifficulty = currentDifficulty === 'easy' ? 'medium' : 
                      currentDifficulty === 'medium' ? 'hard' : 'hard';
    } else if (metrics.averageScore < 50 || metrics.timePerQuestion > 45) {
      // Decrease difficulty if struggling
      newDifficulty = currentDifficulty === 'hard' ? 'medium' : 
                      currentDifficulty === 'medium' ? 'easy' : 'easy';
    }

    setRecommendedDifficulty(newDifficulty);
  }, [recentPerformance, currentDifficulty]);

  return { recommendedDifficulty };
};
